import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ChevronLeft } from 'lucide-react';

const ResearchDetail = () => {
  const { project } = useParams();
  
  const projectData = {
    'neuromorphic-ai': {
      title: 'Neuromorphic AI',
      desc: 'Developing hardware and software architectures inspired by the biological structure of the human brain to achieve unprecedented efficiency and real-time learning capabilities.',
      details: 'Our approach focuses on event-driven computing and spiking neural networks (SNNs) to reduce power consumption by 1000x compared to traditional deep learning models.',
       image: '/images/research/nmai.jpg'
    },
    'hybrid-ai-systems': {
      title: 'Hybrid AI Systems',
      desc: 'Bridging the gap between symbolic reasoning and neural networks to create AI that can both "think" intuitively and "reason" logically.',
      details: 'By integrating large language models with structured knowledge graphs, we are building systems that provide explainable results and maintain high factual accuracy in complex scenarios.',
      image: '/images/research/inf.jpeg'
    },
    'cognitive-neural-networks': {
      title: 'Cognitive Neural Networks',
      desc: 'Advancing neural architectures with integrated memory systems and attention mechanisms that mimic human cognitive functions.',
      details: 'This research explores long-term dependency handling and contextual awareness, allowing AI to maintain consistency over vast temporal scales and multifaceted data inputs.',
      image: '/images/research/cog.jpg'
    },
    'autonomous-robotics': {
      title: 'Autonomous Robotics',
      desc: 'Revolutionary autonomous robotics systems capable of independent navigation, learning, and decision-making in dynamic real-world scenarios.',
      details: 'Our robotics division focuses on sensor fusion and reinforcement learning to allow machines to interact safely and intelligently with their surroundings without human oversight.',
      image: '/images/research/robo.jpg'
    },
    'pryma-h1': {
      title: 'Pryma-H1 Neural Chip',
      desc: 'Next-generation neural processing chip designed specifically for AI workloads, delivering unprecedented computational efficiency and speed.',
      details: 'Pryma-H1 is engineered with a custom architecture that optimizes tensor operations at the silicon level, providing the backbone for the next generation of edge computing.',
      image: '/images/research/chip.jpeg'
    }
  };

  const current = projectData[project] || {
    title: 'Research Project',
    desc: 'Information for this project is currently being updated.',
    details: 'Please check back soon for deeper insights into our latest developments.'
  };

  return (
    <div className="min-h-screen bg-black text-white pt-24">
      <div className="max-w-4xl mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Link to="/" className="inline-flex items-center text-white/50 hover:text-white mb-8 transition-colors">
            <ChevronLeft size={20} className="mr-1" /> Back to Home
          </Link>

          {current.image && (
  <motion.img
    src={current.image}
    alt={current.title}
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6 }}
    className="w-full max-h-72 object-contain rounded-2xl mb-12 bg-black"
  />
)}


          <h1 className="text-5xl md:text-6xl font-bold mb-8">{current.title}</h1>

          <div className="space-y-6 text-white/70 text-lg leading-relaxed mb-12">
            <p className="text-white text-xl font-light">{current.desc}</p>
            <p>{current.details}</p>
            <p>
              This project represents a critical component of our vision to build AI systems that are not only powerful but also safe, reliable, and beneficial to humanity.
            </p>
          </div>

          <Link
            to="/coming-soon"
            className="inline-flex items-center px-8 py-4 bg-white text-black rounded-2xl font-medium hover:scale-[1.02] transition-transform duration-200"
          >
            Deep Dive <ArrowRight size={20} className="ml-2" />
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default ResearchDetail;